'use client';

import { c as _c } from "react/compiler-runtime";
import type { ComponentPropsWithoutRef, CSSProperties, ElementType, ReactNode } from 'react';
import { createElement, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { useTypewriter } from './useTypewriter';
type TypewriterElement = 'span' | 'div';
type RenderArguments = {
  typed: string;
  stable: string;
  recent: string;
  done: boolean;
  skip: () => void;
  prefersReducedMotion: boolean;
};
type BaseElementProperties<E extends ElementType> = Omit<ComponentPropsWithoutRef<E>, 'className'>;
type CommonProperties<E extends ElementType> = {
  text: string;
  className?: string;
  /** ms per char baseline; lower = faster */
  speed?: number;
  showCursor?: boolean;
  ariaLabel?: string;
  as?: E;
  renderContent?: (arguments_: RenderArguments) => ReactNode;
};
export type TypewriterTextProps<E extends ElementType = TypewriterElement> = CommonProperties<E> & BaseElementProperties<E>;
export function TypewriterText(t0) {
  const $ = _c(30);
  const {
    text,
    className,
    speed: t1,
    showCursor: t2,
    ariaLabel,
    as,
    renderContent,
    ...rest
  } = t0;
  const speed = t1 === undefined ? 10 : t1;
  const showCursor = t2 === undefined ? true : t2;
  const element = as ?? "span";
  let t3;
  if ($[0] !== speed) {
    t3 = {
      msPerChar: speed,
      punctuationPauseMs: 70,
      newlinePauseMs: 110,
      recentHighlightCount: 8,
      jitter: 0.18
    };
    $[0] = speed;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const {
    typed,
    stable,
    recent,
    done,
    skip,
    prefersReducedMotion
  } = useTypewriter(text, t3);
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [];
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  useEffect(_temp, t4);
  let content;
  if (renderContent) {
    let t5;
    if ($[3] !== done || $[4] !== prefersReducedMotion || $[5] !== recent || $[6] !== renderContent || $[7] !== skip || $[8] !== stable || $[9] !== typed) {
      t5 = renderContent({
        typed,
        stable,
        recent,
        done,
        skip,
        prefersReducedMotion
      });
      $[3] = done;
      $[4] = prefersReducedMotion;
      $[5] = recent;
      $[6] = renderContent;
      $[7] = skip;
      $[8] = stable;
      $[9] = typed;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    content = t5;
  } else {
    let recentStyle;
    if (!prefersReducedMotion && recent.length > 0) {
      let t5;
      if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
        t5 = {
          animation: "typewriter-ink 90ms ease-out",
          willChange: "filter, transform, opacity"
        };
        $[11] = t5;
      } else {
        t5 = $[11];
      }
      recentStyle = t5 satisfies CSSProperties;
    }
    let t5;
    if ($[12] !== stable) {
      t5 = <span className="opacity-100">{stable}</span>;
      $[12] = stable;
      $[13] = t5;
    } else {
      t5 = $[13];
    }
    const t6 = !prefersReducedMotion && "inline-block [text-shadow:00.35em_0.6em_rgba(80,165,255,0.25)]";
    let t7;
    if ($[14] !== t6) {
      t7 = cn(t6);
      $[14] = t6;
      $[15] = t7;
    } else {
      t7 = $[15];
    }
    let t8;
    if ($[16] !== recent || $[17] !== recentStyle || $[18] !== t7) {
      t8 = <span className={t7} style={recentStyle}>{recent}</span>;
      $[16] = recent;
      $[17] = recentStyle;
      $[18] = t7;
      $[19] = t8;
    } else {
      t8 = $[19];
    }
    let t9;
    if ($[20] !== t5 || $[21] !== t8) {
      t9 = <>{t5}{t8}</>;
      $[20] = t5;
      $[21] = t8;
      $[22] = t9;
    } else {
      t9 = $[22];
    }
    content = t9;
  }
  let cursorStyle;
  if (showCursor) {
    let animation;
    if (done || prefersReducedMotion) {
      animation = prefersReducedMotion ? "none" : "typewriter-caret-idle 1400ms ease-in-out infinite";
    } else {
      animation = "typewriter-caret-move 420ms cubic-bezier(0.64,0,0.34,1) infinite alternate, typewriter-caret-glow 1500ms ease-in-out infinite, typewriter-caret-blink 780ms steps(1,end) infinite";
    }
    let boxShadow;
    if (!prefersReducedMotion) {
      boxShadow = done ? "0 0 0.35em rgba(80,165,255,0.35)" : "0 0 0.65em rgba(80,165,255,0.55)";
    }
    const t5 = done ? 0.4 : 1;
    let t6;
    if ($[23] !== animation || $[24] !== boxShadow || $[25] !== t5) {
      t6 = {
        animation,
        opacity: t5,
        width: "0.55ch",
        boxShadow,
        borderRadius: "1px",
        transformOrigin: "center bottom"
      };
      $[23] = animation;
      $[24] = boxShadow;
      $[25] = t5;
      $[26] = t6;
    } else {
      t6 = $[26];
    }
    cursorStyle = t6;
  }
  let t5;
  if ($[27] !== cursorStyle || $[28] !== showCursor) {
    t5 = showCursor ? <span className="ml-[1px] inline-block translate-y-[1px] border-r-2 border-current" aria-hidden="true" style={cursorStyle} /> : null;
    $[27] = cursorStyle;
    $[28] = showCursor;
    $[29] = t5;
  } else {
    t5 = $[29];
  }
  return createElement(element, {
    ...rest,
    className: cn("relative inline-block whitespace-pre-wrap break-words [font-variant-ligatures:contextual]", className),
    "aria-label": ariaLabel,
    role: "status"
  }, content, t5);
}
function _temp() {
  ensureTypewriterStyles();
}
type TypewriterStylesStatus = 'pending' | 'ready';
let typewriterStylesStatus: TypewriterStylesStatus = 'pending';
function ensureTypewriterStyles() {
  if (typewriterStylesStatus === 'ready') {
    return;
  }
  const documentReference = globalThis.document ?? null;
  if (!documentReference?.head) {
    return;
  }
  if (documentReference.head.querySelector('style[data-typewriter-text]')) {
    typewriterStylesStatus = 'ready';
    return;
  }
  typewriterStylesStatus = 'ready';
  const styleElement = document.createElement('style');
  const dataset = styleElement.dataset as DOMStringMap & {
    typewriterText?: string;
  };
  dataset.typewriterText = '';
  styleElement.textContent = `
@keyframes typewriter-ink {
  from {
    opacity: 0.2;
    filter: blur(1px);
    transform: translateY(0.12em);
  }
  to {
    opacity: 1;
    filter: none;
    transform: none;
  }
}

@keyframes typewriter-blink {
  0%,
  49% {
    opacity: 1;
  }
  50%,
  100% {
    opacity: 0;
  }
}`;
  documentReference.head.append(styleElement);
}